import { useTranslation } from "react-i18next";
import { AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { TruncatedText } from "@/components/ui";
import { appEnvironment } from "@/lib/appEnvironment";
import { ENVIRONMENT_BANNER_HEIGHT_CLASS } from "./environmentBannerLayout";
import { useEnvironmentBannerState } from "@/hooks/useEnvironmentBannerState";

export function EnvironmentBanner() {
  const { t } = useTranslation("common");
  const { visible, showProductionAdminBanner } = useEnvironmentBannerState();

  if (!visible) {
    return null;
  }

  const environmentFallback =
    appEnvironment.charAt(0).toUpperCase() + appEnvironment.slice(1);

  const environmentName = t(`environmentBanner.envNames.${appEnvironment}`, {
    defaultValue: environmentFallback,
  });

  const shortEnvironmentName = t(
    `environmentBanner.shortEnvNames.${appEnvironment}`,
    { defaultValue: environmentFallback },
  );

  const fullMessage = showProductionAdminBanner
    ? t("environmentBanner.productionMessage")
    : t("environmentBanner.message", { environment: environmentName });

  const shortMessage = showProductionAdminBanner
    ? t("environmentBanner.productionMessageShort")
    : t("environmentBanner.messageShort", { environment: shortEnvironmentName });

  const isWarningVariant = showProductionAdminBanner;
  const BannerIcon = isWarningVariant ? AlertTriangle : Info;

  return (
    <>
      <div
        role="status"
        className={cn(
          "fixed inset-x-0 top-0 z-50 flex items-center border-b px-4 text-center text-xs font-medium",
          ENVIRONMENT_BANNER_HEIGHT_CLASS,
          isWarningVariant
            ? "border-warning-fill/25 bg-warning-400 text-warning-ink"
            : "border-primary-600/15 bg-[linear-gradient(90deg,var(--color-primary-50),var(--color-primary-100),var(--color-primary-50))] text-primary-800",
        )}
      >
        <div className="mx-auto flex w-full min-w-0 max-w-7xl items-center justify-center gap-2 px-1">
          <BannerIcon
            className="h-3.5 w-3.5 shrink-0"
            aria-hidden
          />
          <TruncatedText className="sm:hidden">{shortMessage}</TruncatedText>
          <span className="hidden sm:inline">{fullMessage}</span>
        </div>
      </div>
      <div
        className={ENVIRONMENT_BANNER_HEIGHT_CLASS}
        aria-hidden
      />
    </>
  );
}
